import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subscription, delay } from 'rxjs';

/**
 * Sidebar stats for the interactive radial chart
 */
export interface ITimelineSidebarStats {
  label: string;
  value: string | number;
  percentage: number;
}

/**
 * Sidebar data configuration
 */
export interface ITimelineSidebarData {
  totalRevenue: string;
  revenueLabel: string;
  dailyCronRevenue?: string;
  tryAgainRevenue?: string;
  stats: ITimelineSidebarStats[];
  /** Optional base color for the sidebar chart. Defaults to config.themeColor */
  themeColor?: string;
}

/**
 * Data point for the timeline (Original Version)
 */
export interface ITimelineDataPoint {
  /** The year or primary label on the bubble (e.g. 2014, 2015) */
  year: string | number;
  /** The large indicator number (e.g. 01, 02) */
  value: string;
  /** A short label below the value */
  label: string;
  /** A longer description text */
  description?: string;
  /** Custom color for this point (bubble and dot) */
  color?: string;
  /** Force position - 'top' or 'bottom'. If not provided, will alternate */
  position?: 'top' | 'bottom';
  /** Flag for growth indicators */
  growthIsPositive?: boolean;
}

/**
 * Configuration for the timeline chart
 */
export interface ITimelineConfig {
  /** Main title on the left */
  title?: string;
  /** Subtitle on the left */
  subtitle?: string;
  /** Default color for bubbles and dots */
  themeColor?: string;
  /** Axis line color */
  axisColor?: string;
  /** Whether to animate the dots and bubbles */
  animate?: boolean;
}

@Component({
  selector: 'app-timeline-chart',
  templateUrl: './timeline-chart.component.html',
  styleUrls: ['./timeline-chart.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class TimelineChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  @ViewChild('sidebarChartContainer') sidebarChartContainer!: ElementRef;

  // ============ INPUTS ============
  /** Timeline data points */
  @Input() data: ITimelineDataPoint[] = [];
  /** Chart configuration */
  @Input() config: ITimelineConfig = {
    title: 'Statistic Graph',
    subtitle: 'All mentions:',
    themeColor: '#3b82f6',
    axisColor: '#e2e8f0',
    animate: true
  };
  /** Additional filters for the sidebar */
  @Input() filters: { label: string, options: string[] }[] = [];
  /** Sidebar chart data */
  @Input() sidebarData: ITimelineSidebarData = {
    totalRevenue: '$10,00,000.00',
    revenueLabel: 'Total Revenue',
    stats: [
      { label: 'Total Payments', value: 100, percentage: 85 },
      { label: 'Success', value: 80, percentage: 65 },
      { label: 'Failed', value: 20, percentage: 35 }
    ]
  };

  // ============ PRIVATE PROPERTIES ============
  private svg: any;
  private themeService = inject(ThemeService);
  private themeSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private isFirstRender = true;

  // ============ LIFECYCLE HOOKS ============
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['data'] || (changes as any)['config'] || changes['filters'] || changes['sidebarData']) && !this.isFirstRender) {
      this.createTimeline();
      this.createSidebarChart();
    }
  }

  ngAfterViewInit(): void {
    this.themeSubscription = this.themeService.theme$.subscribe(() => {
      this.createTimeline();
    });

    let resizeTimer: any;
    this.resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to prevent ResizeObserver loop errors 
      // and ensure layout is stable before redrawing
      cancelAnimationFrame(resizeTimer);
      resizeTimer = requestAnimationFrame(() => {
        this.createTimeline();
        this.createSidebarChart();
      });
    });

    if (this.chartContainer?.nativeElement) {
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }

    // Initial render
    requestAnimationFrame(() => {
      this.createTimeline();
      this.createSidebarChart();
      this.isFirstRender = false;
    });
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  // ============ RENDERING LOGIC ============
  private createTimeline(): void {
    const container = this.chartContainer.nativeElement;
    d3.select(container).selectAll('*').remove();

    if (!this.data || this.data.length === 0) return;

    const width = container.clientWidth;
    const height = container.clientHeight;
    // Dynamic padding and center based on screen size
    const isMobile = width < 640;
    const padding = isMobile ? 15 : 30; // Reduced from 30/60
    const centerY = height / 2;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('overflow', 'visible');

    this.svg = svg;

    // 1. Draw central axis line
    svg.append('line')
      .attr('x1', padding)
      .attr('y1', centerY)
      .attr('x2', width - padding)
      .attr('y2', centerY)
      .attr('stroke', this.config.axisColor || '#e2e8f0')
      .attr('stroke-width', 2);

    // 2. Calculate positions
    const xScale = d3.scaleLinear()
      .domain([0, this.data.length - 1])
      .range([padding + (isMobile ? 15 : 30), width - padding - (isMobile ? 15 : 30)]);

    // 3. Draw points and bubbles
    this.data.forEach((point, i) => {
      const x = xScale(i);
      const isTop = point.position ? point.position === 'top' : i % 2 !== 0;
      const color = point.color || this.config.themeColor || '#3b82f6';

      const pointGroup = svg.append('g')
        .attr('class', `timeline-point-${i}`)
        .style('cursor', 'default');

      // A. Shadow dot / Glow
      pointGroup.append('circle')
        .attr('cx', x)
        .attr('cy', centerY)
        .attr('r', isMobile ? 6 : 8)
        .attr('fill', color)
        .attr('opacity', 0.2);

      // B. Main dot on axis
      pointGroup.append('circle')
        .attr('cx', x)
        .attr('cy', centerY)
        .attr('r', isMobile ? 4 : 6)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);

      // C. Connecting vertical line
      const lineLen = isMobile ? 15 : 18;
      const yEndBubble = isTop ? centerY - lineLen : centerY + lineLen;

      pointGroup.append('line')
        .attr('x1', x)
        .attr('y1', centerY)
        .attr('x2', x)
        .attr('y2', yEndBubble)
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('opacity', 0.5);

      // D. HTML Bubble - scaled for mobile
      const bubbleW = isMobile ? 90 : 110;
      const bubbleH = isMobile ? 90 : 110;
      const foX = x - bubbleW / 2;
      const foY = isTop ? centerY - bubbleH - (isMobile ? 8 : 15) : centerY + (isMobile ? 8 : 15);

      const fo = svg.append('foreignObject')
        .attr('x', foX)
        .attr('y', foY)
        .attr('width', bubbleW)
        .attr('height', bubbleH)
        .style('overflow', 'visible');

      const bubbleDiv = fo.append('xhtml:div')
        .attr('class', `flex flex-col items-center justify-center text-center w-full h-full transition-all duration-300 ${isTop ? 'flex-col-reverse' : 'flex-col'}`);

      // Year/Label Badge - increased margin
      const badgeClass = isTop ? 'mb-3' : 'mt-3';
      bubbleDiv.append('xhtml:div')
        .attr('class', `${isMobile ? 'px-2 py-0.5 text-[10px]' : 'px-4 py-1.5 text-sm'} rounded-full text-white font-semibold shadow-lg ${badgeClass}`)
        .style('background', color)
        .style('box-shadow', `0 4px 12px ${color}44`)
        .text(point.year);

      // Value and Description
      const contentGroup = bubbleDiv.append('xhtml:div')
        .attr('class', 'flex flex-col items-center');

      contentGroup.append('xhtml:div')
        .attr('class', `${isMobile ? 'text-base' : 'text-xl'} font-bold text-slate-800 dark:text-slate-100 leading-tight`)
        .text(point.value);

      contentGroup.append('xhtml:div')
        .attr('class', `${isMobile ? 'text-[9px]' : 'text-sm'} text-slate-700 dark:text-slate-300 tracking-tight`)
        .style('font-weight', '550')
        .style('color', '#636363ff')
        .text(point.label);

      if (point.description && !isMobile) {
        const growthColor = point.growthIsPositive === true ? '#22c55e' : (point.growthIsPositive === false ? '#ef4444' : '#94a3b8');
        contentGroup.append('xhtml:div')
          .attr('class', 'text-[10px] font-semibold px-1 mt-0.5')
          .style('color', growthColor)
          .text(point.description);
      }

      // Animation
      if (this.config.animate) {
        pointGroup.attr('opacity', 0)
          .transition()
          .delay(i * 150)
          .duration(500)
          .attr('opacity', 1);

        fo.attr('opacity', 0)
          .style('transform', `translateY(${isTop ? '10px' : '-10px'})`)
          .transition()
          .delay(i * 150 + 200)
          .duration(500)
          .attr('opacity', 1)
          .style('transform', 'translateY(0)');
      }
    });
  }

  private createSidebarChart(): void {
    if (!this.sidebarChartContainer) return;
    const container = this.sidebarChartContainer.nativeElement;
    d3.select(container).selectAll('*').remove();

    const width = container.clientWidth;
    const height = container.clientHeight || 300;
    const centerX = width / 2;
    // Expanded radius to utilize full available width
    const radius = Math.min(width / 2 - 30, height / 2.4);
    const sectionGap = 3;

    // Position centerY to maximize visibility
    const centerY = (height / 2) - 10;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const mainGroup = svg.append('g')
      .attr('transform', `translate(${centerX}, ${centerY})`);

    const barWeight = 10;
    const barSpacing = 6;
    const topBaseRadius = radius;
    const baseColor = (this.sidebarData as any).themeColor || this.config.themeColor || '#3e227e';

    this.sidebarData.stats.forEach((stat, i) => {
      const outerR = topBaseRadius - i * (barWeight + barSpacing);
      const innerR = outerR - barWeight;
      const sweep = (stat.percentage / 100) * Math.PI;

      // Monochromatic opacity logic
      const opacity = 0.8 - (i * 0.25);

      const trackArc = d3.arc()
        .innerRadius(innerR)
        .outerRadius(outerR)
        .startAngle(Math.PI * 1.5) // Left
        .endAngle(Math.PI * 2.5);  // Right

      mainGroup.append('path')
        .attr('d', trackArc as any)
        .attr('fill', '#334155')
        .attr('opacity', 0.1) // Subtle tracks
        .attr('transform', `translate(0, -${sectionGap / 2})`);

      const startAngle = Math.PI * 1.5;
      const endAngle = Math.PI * 1.5 + sweep;

      const progressArc = d3.arc()
        .innerRadius(innerR)
        .outerRadius(outerR)
        .startAngle(startAngle)
        .endAngle(endAngle)
        .cornerRadius(5);

      const path = mainGroup.append('path')
        .attr('fill', baseColor)
        .attr('opacity', opacity)
        .attr('transform', `translate(0, -${sectionGap / 2})`);

      // Left chart (sidebar) should not have animation as per user request
      path.attr('d', progressArc({ startAngle: startAngle, endAngle: endAngle } as any));
    });

    // --- 2. BOTTOM SECTION: Square top, Rounded bottom Rectangle ---
    const rectW = radius * 2;
    const rectH = 120;
    const rectX = -rectW / 2;
    const rectY = sectionGap / 2;
    const r = 6;

    // Custom path for 0 radius top, 6px radius bottom
    const rectPath = `
      M ${rectX},${rectY}
      L ${rectX + rectW},${rectY}
      L ${rectX + rectW},${rectY + rectH - r}
      A ${r},${r} 0 0 1 ${rectX + rectW - r},${rectY + rectH}
      L ${rectX + r},${rectY + rectH}
      A ${r},${r} 0 0 1 ${rectX},${rectY + rectH - r}
      Z
    `;

    mainGroup.append('path')
      .attr('d', rectPath)
      .attr('fill', baseColor);

    // Revenue Display (Higher up in rectangle)
    const revenueGroup = mainGroup.append('g')
      .attr('transform', `translate(0, ${rectY + 28})`)
      .attr('text-anchor', 'middle');

    revenueGroup.append('text')
      .attr('fill', '#fff')
      .attr('y', -4)
      .attr('dominant-baseline', 'middle')
      .attr('style', 'font-size: 20px; font-weight: 800;')
      .text(this.sidebarData.totalRevenue);

    revenueGroup.append('text')
      .attr('fill', 'rgba(255,255,255,0.7)')
      .attr('y', 20)
      .attr('style', 'font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em;')
      .text(this.sidebarData.revenueLabel);

    // Integrated Legend
    const legendGroup = mainGroup.append('g')
      .attr('transform', `translate(0, ${rectY + 68})`);

    this.sidebarData.stats.forEach((stat, i) => {
      const rowY = i * 18;
      const opacity = 0.8 - (i * 0.25);
      const rowX = -rectW / 2 + 10;

      const row = legendGroup.append('g')
        .attr('transform', `translate(${rowX}, ${rowY})`);

      // Dot
      row.append('circle')
        .attr('cx', 4)
        .attr('cy', 0)
        .attr('r', 2.5)
        .attr('fill', '#ffffff')
        .attr('opacity', opacity);

      // Label - increased size to 12px for better readability
      row.append('text')
        .attr('x', 14)
        .attr('y', 0)
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'rgba(255,255,255,0.75)')
        .attr('style', 'font-size: 12px; font-weight: 600;')
        .text(stat.label);

      // Value - increased size to 12px for better readability
      row.append('text')
        .attr('x', rectW - 15)
        .attr('y', 0)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#ffffff')
        .attr('style', 'font-size: 12px; font-weight: 800;')
        .text(stat.value);
    });
  }
}
